import { Children, cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react'
import cx from 'classnames'
import { createPortal } from 'react-dom'

import { ButtonToneContext, IconButtonToneContext } from '../contexts'
import { ModalHeader } from './ModalHeader'

const MODAL_TRANSITION_MS = 200
const modalStack = []
let bodyOverflowBeforeModals = ''

const getFocusableElements = container => Array.from(container?.querySelectorAll(
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
) || []).filter(element => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true')

export const Modal = ({ children, className, isOpen, label = 'Dialog', labelledBy, size = 'md', toggle }) => {
  const [isMounted, setIsMounted] = useState(isOpen)
  const [isVisible, setIsVisible] = useState(false)
  const dialogRef = useRef(null)
  const previousFocusRef = useRef(null)
  const toggleRef = useRef(toggle)
  const titleId = useId()
  const hasHeader = Children.toArray(children).some(child => isValidElement(child) && child.type === ModalHeader)
  const accessibleTitleId = labelledBy || (hasHeader ? titleId : undefined)

  useEffect(() => {
    toggleRef.current = toggle
  }, [toggle])

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true)
      const frame = requestAnimationFrame(() => setIsVisible(true))
      return () => cancelAnimationFrame(frame)
    }

    setIsVisible(false)
    const timeout = setTimeout(() => setIsMounted(false), MODAL_TRANSITION_MS)
    return () => clearTimeout(timeout)
  }, [isOpen])

  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    const modalId = Symbol('modal')
    if (modalStack.length === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }

    modalStack.push(modalId)
    previousFocusRef.current = document.activeElement
    const focusableElements = getFocusableElements(dialogRef.current)
    const initialFocusTarget = focusableElements[0] || dialogRef.current

    initialFocusTarget?.focus()

    const handleKeyDown = event => {
      if (modalStack[modalStack.length - 1] !== modalId) return

      if (event.key === 'Escape') {
        toggleRef.current?.()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const elements = getFocusableElements(dialogRef.current)
      if (elements.length === 0) {
        event.preventDefault()
        dialogRef.current?.focus()
        return
      }

      const firstElement = elements[0]
      const lastElement = elements[elements.length - 1]
      if (!dialogRef.current?.contains(document.activeElement)) {
        event.preventDefault()
        firstElement.focus()
        return
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      const wasTopModal = modalStack[modalStack.length - 1] === modalId
      const modalIndex = modalStack.indexOf(modalId)
      if (modalIndex !== -1) modalStack.splice(modalIndex, 1)
      if (modalStack.length === 0) document.body.style.overflow = bodyOverflowBeforeModals
      if (!wasTopModal) return

      if (previousFocusRef.current && document.body.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus?.()
        return
      }

      const fallbackFocusTarget = document.querySelector('main h1, main')
      if (fallbackFocusTarget) {
        if (!fallbackFocusTarget.hasAttribute('tabindex')) fallbackFocusTarget.setAttribute('tabindex', '-1')
        fallbackFocusTarget.focus()
      }
    }
  }, [isVisible])

  if (!isMounted) {
    return null
  }

  const widthClassName = size === 'lg' ? 'tw:max-w-[760px]' : 'tw:max-w-[560px]'

  return createPortal(
    <ButtonToneContext.Provider value="default">
      <IconButtonToneContext.Provider value="default">
        <div
          className={cx(
            'ds-modal-backdrop tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center tw:bg-overlay tw:p-4 tw:backdrop-blur-[1px] tw:mobile:items-end tw:mobile:p-0',
            isVisible ? 'tw:opacity-100' : 'tw:opacity-0',
          )}
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              toggle?.()
            }
          }}
        >
          <div
            aria-label={accessibleTitleId ? undefined : label}
            aria-labelledby={accessibleTitleId}
            aria-modal="true"
            className={cx(
              'ds-modal-dialog tw:flex tw:max-h-[calc(100vh-40px)] tw:w-full tw:flex-col tw:overflow-hidden tw:rounded-modal tw:bg-surface-raised tw:shadow-lg tw:outline-none',
              widthClassName,
              className,
            )}
            ref={dialogRef}
            role="dialog"
            tabIndex={-1}
          >
            {Children.map(children, child => (
              isValidElement(child) && child.type === ModalHeader
                ? cloneElement(child, { id: titleId, toggle: child.props.toggle || toggle })
                : child
            ))}
          </div>
        </div>
      </IconButtonToneContext.Provider>
    </ButtonToneContext.Provider>,
    document.body,
  )
}
